import {
  useCatalogItem,
  useCatalogList,
  useCreateSpecimen,
  useMeta,
  DataError,
} from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
} from '@numistic/ui'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'

import { PageLoading } from '../../components/page-loading.js'
import { cleanEvent, EventFields, type EventDraft } from '../../collections/event-fields.js'
import { useErrorText } from '../../lib/errors.js'
import { requireSession } from '../../lib/guards.js'
import { MetaForm } from '../../meta/meta-form.js'
import { useRefResolver } from '../../meta/ref-resolver.js'
import { ExtraRefOptions } from '../../meta/widgets/ref-options.js'
import { rootRoute } from '../root.js'

export const specimenNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/s/new',
  validateSearch: z.object({
    catalogItem: z.string().optional().catch(undefined),
    collection: z.string().optional().catch(undefined),
  }),
  beforeLoad: ({ context, location }) => requireSession(context, location),
  component: NewSpecimenPage,
})

function NewSpecimenPage() {
  const { t } = useTranslation()
  const search = specimenNewRoute.useSearch()
  const navigate = useNavigate({ from: '/s/new' })
  return (
    <div className="grid max-w-4xl gap-6">
      <h1 className="text-2xl font-semibold">{t('specimen.newTitle')}</h1>
      {search.catalogItem ? (
        <SpecimenDetails
          catalogItemId={search.catalogItem}
          collectionId={search.collection}
          onChangeItem={() => navigate({ search: (s) => ({ ...s, catalogItem: undefined }) })}
        />
      ) : (
        <CatalogPicker onPick={(id) => navigate({ search: (s) => ({ ...s, catalogItem: id }) })} />
      )}
    </div>
  )
}

const ISSUERS = new Set(['issuers'])

function CatalogPicker({ onPick }: { onPick: (id: string) => void }) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const list = useCatalogList({ q: q.trim() || undefined, limit: 20 })
  const items = list.data?.pages.flatMap((p) => p.items) ?? []
  const meta = useMeta().data
  const resolve = useRefResolver(meta, ISSUERS)
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('specimen.pickCatalog')}</CardTitle>
        <CardDescription>{t('specimen.pickCatalogHint')}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <Input
          type="search"
          autoFocus
          aria-label={t('common.search')}
          placeholder={t('catalog.searchPlaceholder')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {list.isSuccess && !items.length ? (
          <p className="text-sm text-muted-foreground">{t('catalog.empty')}</p>
        ) : null}
        <ul className="grid gap-1" aria-label={t('specimen.pickCatalog')}>
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-accent"
                onClick={() => onPick(item.id)}
              >
                <span className="size-10 shrink-0 overflow-hidden rounded bg-muted">
                  {item.thumb ? (
                    <img src={item.thumb} alt="" className="size-full object-contain" />
                  ) : null}
                </span>
                <span className="grid min-w-0">
                  <span className="truncate text-sm">{item.title}</span>
                  <span className="text-xs text-muted-foreground">
                    {[
                      resolve('issuers', String(item.values.issuer ?? '')),
                      item.visibility === 'private' ? t('catalog.visibilities.private') : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to="/catalog/import/numista" search={q.trim().length >= 2 ? { q: q.trim() } : {}}>
              {t('specimen.findInNumista')}
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/catalog/new" search={{}}>
              {t('specimen.createPrivate')}
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function SpecimenDetails({
  catalogItemId,
  collectionId,
  onChangeItem,
}: {
  catalogItemId: string
  collectionId?: string
  onChangeItem: () => void
}) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const meta = useMeta().data
  const item = useCatalogItem(catalogItemId)
  const create = useCreateSpecimen()
  const navigate = useNavigate()
  const [overrides, setOverrides] = useState(false)
  const [acquisition, setAcquisition] = useState<EventDraft>({ occurredOn: '', currency: 'RUB' })
  if (item.error) return <Alert variant="destructive">{errorText.request(item.error)}</Alert>
  if (!item.data || !meta) return <PageLoading />
  const inCollection = meta.statuses.find((s) => s.code === 'in_collection')?.id
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('specimen.details')}</CardTitle>
        <CardDescription className="flex flex-wrap items-center gap-2">
          {t('specimen.fromCatalog')}: <strong>{String(item.data.values.title ?? '')}</strong>
          <Button variant="link" size="sm" onClick={onChangeItem}>
            {t('specimen.change')}
          </Button>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ExtraRefOptions.Provider
          value={{ variants: item.data.variants.map((v) => ({ id: v.id, label: v.label })) }}
        >
          <MetaForm
            typeCode={item.data.typeCode}
            level="specimen"
            includeOverrides={overrides}
            inherited={item.data.values}
            sectionOrder={['specimen', 'main', 'physical', 'paper', 'design', 'notes']}
            defaultValues={{ collection: collectionId, status: inCollection, quantity: 1 }}
            serverIssues={create.error instanceof DataError ? create.error.issues : undefined}
            submitLabel={t('specimen.create')}
            header={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="justify-self-start"
                onClick={() => setOverrides((v) => !v)}
              >
                {overrides ? t('specimen.hideOverrides') : t('specimen.showOverrides')}
              </Button>
            }
            beforeSubmit={
              <fieldset className="grid gap-3">
                <legend className="mb-1 text-sm font-semibold text-muted-foreground">
                  {t('specimen.acquisition')}
                </legend>
                <p className="text-xs text-muted-foreground">{t('specimen.acquisitionHint')}</p>
                <EventFields meta={meta} value={acquisition} onChange={setAcquisition} />
              </fieldset>
            }
            footer={
              create.error ? (
                <Alert variant="destructive">{errorText.request(create.error)}</Alert>
              ) : null
            }
            pending={create.isPending}
            onSubmit={(values) =>
              create.mutate(
                { catalogItemId, values, acquisition: cleanEvent(acquisition) },
                { onSuccess: (s) => void navigate({ to: '/s/$id', params: { id: s.id } }) },
              )
            }
          />
        </ExtraRefOptions.Provider>
      </CardContent>
    </Card>
  )
}
